import React, { useState, useEffect } from "react";
import "../JobPosting/jobs.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchJobById, updateJob } from "../../Redux/ActionCreators/jobsAction";
import { updateAssessmentSettings } from "../../Redux/ActionCreators/testLibraryAction";

const CandidateReportSharing = ({ setType, setSnackbar }) => {
  const { jobId } = useParams();
  const dispatch = useDispatch();

  const [saveLoading, setSaveLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enableShare, setEnableShare] = useState(false);
  const [hideDetails, setHideDetails] = useState(false);
  const [enableReapply, setEnableReapply] = useState(false);
  const [maxApplications, setMaxApplications] = useState(1);
  const [cooldownDays, setCooldownDays] = useState(0);
  const [cvMode, setCvMode] = useState("mandatory");

  const job = useSelector((state) => state.jobs.latestJob);

  const initialfetch = async () => {
    if (jobId) {
      setLoading(true);
      await dispatch(fetchJobById(jobId));
      setLoading(false);
    }
  };

  useEffect(() => {
    initialfetch();
  }, [jobId]);

  useEffect(() => {
    if (job) {
      setEnableShare(job?.enable_candidate_report_share ?? false);
      setHideDetails(job?.hide_candidate_details ?? false);

      const reapplySettings = job?.candidate_reapply_settings;
      setEnableReapply(reapplySettings?.enabled ?? false);
      setMaxApplications(reapplySettings?.max_applications ?? 1);
      setCooldownDays(reapplySettings?.cooldown_days ?? 0);

      setCvMode(job?.web_chat_settings?.cv_mode ?? "mandatory");
    }
  }, [job]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSave = async () => {
    if (!jobId) return;

    setSaveLoading(true);

    const payload = {
      enable_candidate_report_share: enableShare,
      hide_candidate_details: hideDetails,
      candidate_reapply_settings: {
        enabled: enableReapply,
        max_applications: Number(maxApplications) || 1,
        cooldown_days: Number(cooldownDays) || 0,
      },
      web_chat_settings: {
        cv_mode: cvMode,
      },
    };

    const res = await dispatch(updateJob(jobId, payload));

    // When candidate reapply is enabled, sync the attempt limit to the
    // linked assessment settings using the job's assessment_id as testId.
    const testId = job?.assessment_id;
    if (enableReapply && testId) {
      try {
        await dispatch(
          updateAssessmentSettings({
            attemptLimit: Number(maxApplications) || 1,
            testId,
          })
        );
      } catch (err) {
        setSaveLoading(false);
        showSnackbar("Failed to save assessment settings", "error");
        return;
      }
    }

    setSaveLoading(false);

    if (res?.success === false) {
      showSnackbar("Failed to save settings", "error");
      return;
    }

    // SUCCESS
    showSnackbar("Settings saved successfully", "success");

    // Navigate immediately
    setType("");
  };

  return (
    <div className="job-prescreening">
      <div className="job-settings-head">
        <img
          src="/images/back-arrow-black.svg"
          alt="back"
          width={32}
          height={32}
          onClick={() => {
            setType("");
          }}
          style={{ cursor: "pointer" }}
        />
        <div className="job-settings-head-title">
          <h3>Candidate Settings</h3>
        </div>
      </div>

      <div className="job-settings-body">
        {loading ? (
          <div
            className="loading-overlay"
            style={{
              position: "relative",
              marginTop: 16,
              alignItems: "start",
              height: "32px",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="job-settings-form">
            <div className="vs-gappeo-card">
              <div className="vs-gappeo-card-info">
                <h5>Candidate reapply</h5>
              </div>
              <label className="toggle-switch toggle-pink">
                <input
                  type="checkbox"
                  checked={enableReapply}
                  onChange={(e) => setEnableReapply(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>

            {enableReapply && (
              <div className="job-settings-form-header">
                <div className="job-settings-form-score">
                  <label>Max applications</label>
                  <input
                    type="number"
                    min={1}
                    value={maxApplications}
                    onChange={(e) => setMaxApplications(e.target.value)}
                  />
                </div>
                <div className="job-settings-form-score">
                  <label>Cooldown days</label>
                  <input
                    type="number"
                    min={0}
                    value={cooldownDays}
                    onChange={(e) => setCooldownDays(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="job-settings-form-answer">
              <label>CV upload mode</label>
              <select
                value={cvMode}
                onChange={(e) => setCvMode(e.target.value)}
              >
                <option value="mandatory">Mandatory</option>
                <option value="optional">Optional</option>
                <option value="skip">Skip</option>
              </select>
            </div>

            <div className="job-settings-form-header" style={{ flexDirection: "column" }}>
              <div className="job-settings-form-labels">
                <input
                  type="checkbox"
                  checked={enableShare}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setEnableShare(val);
                    if (!val) {
                      setHideDetails(false);
                    }
                  }}
                />
                <label>Enable candidate report share access</label>
              </div>
              <div className="job-settings-form-labels">
                <input
                  type="checkbox"
                  disabled={!enableShare}
                  checked={hideDetails}
                  onChange={(e) => setHideDetails(e.target.checked)}
                />
                <label>Hide candidate details in shared report</label>
              </div>
            </div>

            <button
              className="save-btn"
              disabled={saveLoading}
              onClick={handleSave}
            >
              {saveLoading ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateReportSharing;
